// Inferring an item's kind instead of asking for it.
// docs/operational-ux-2026-09/06-menu-architecture.md §3.2
//
// ─── WHY ─────────────────────────────────────────────────────────────────────
//
// The Paris Place field study counted 27 fields between a merchant and "jollof
// rice, ₵70". The very first one was a taxonomy question — a row of kind chips
// (Dish · Drink · Package · Service · Combo) above the name field — asked before
// the merchant had typed a character. It is the worst possible opening question:
// it is about OUR data model, the merchant has no vocabulary for it, and in the
// overwhelming majority of cases the answer is already determined by facts we
// hold.
//
// So we stop asking. `relevance.ts` already knows which kinds a branch may use;
// this module uses that same knowledge to ANSWER the question rather than to
// decorate it. The kind stays fully editable under More options → Advanced, and
// an existing item's stored kind is never overwritten — inference runs once, on
// a brand-new item, exactly where the chip row used to be.
//
// ─── THE RULE ────────────────────────────────────────────────────────────────
//
//   kind := the chosen section's reporting class   (Drinks → drink)
//         ∧ the business type's own default        (a caterer → package)
//         ∧ 'dish'
//
// each step filtered by what `resolveKindRelevance` says the branch is allowed
// to use, so a caterer without bar service filing something under "Drinks" gets
// a package, not a kind their editor cannot render.
//
// ─── WHAT IS DELIBERATELY *NOT* INFERRED ─────────────────────────────────────
//
// `combo`. A sub-type declaring `primaryItemKinds: ['combo']` (fast_food,
// street_food) means combos are CENTRAL to that business, not that every item is
// one — Paris Place sells far more single plates than meal deals. Defaulting to
// combo there would make the common case wrong and force the merchant back into
// the taxonomy picker we just removed. Doc 06 §3.2 gives combos their own door
// instead: a "Build a combo" entry point beside "Add item", which
// `shouldOfferComboEntry` below answers.
//
// Client-safe: same constraint as the rest of this folder.

import { inferMenuCategoryType, type MenuCategoryType } from '@/lib/menu/category-type';

import { defaultItemKindForBusinessType } from './default-kind';
import { resolveKindRelevance, type ActiveMode } from './relevance';
import { DEFAULT_ITEM_KIND, type ItemKind } from './types';

export interface InferItemKindContext {
  typeId: string | null | undefined;
  specificType?: string | null;
  activeModes?: readonly ActiveMode[];
  /**
   * The reporting class of the section the item is being filed under
   * (`menu_categories.category_type`). This is the strongest signal we have:
   * a merchant who picked "Drinks" has already told us what the thing is.
   */
  categoryType?: MenuCategoryType | string | null;
  /**
   * The section's name, used only when `categoryType` is absent. Every category
   * row has a reporting class (it defaults to `food`), but a category created
   * before migration 20260521010000 backfilled names can still read `food` while
   * being called "Soft Drinks" — and a caller holding a half-populated row is
   * better served by the same name-matching the migration itself used.
   */
  categoryName?: string | null;
}

function normalizeCategoryType(
  categoryType: MenuCategoryType | string | null | undefined,
  categoryName: string | null | undefined,
): MenuCategoryType | null {
  if (categoryType === 'drink' || categoryType === 'other' || categoryType === 'food') {
    // A category explicitly classed `food` still loses to its own name when the
    // name is unambiguous — see the doc comment on `categoryName`.
    if (categoryType === 'food' && categoryName) {
      return inferMenuCategoryType(categoryName);
    }
    return categoryType;
  }
  if (categoryName) return inferMenuCategoryType(categoryName);
  return null;
}

/**
 * The kind a brand-new item should be created as. Never throws, always returns a
 * kind the branch is allowed to author.
 */
export function inferItemKind(ctx: InferItemKindContext): ItemKind {
  const { allowed } = resolveKindRelevance({
    typeId: ctx.typeId,
    specificType: ctx.specificType,
    activeModes: ctx.activeModes,
  });
  const permitted = new Set<ItemKind>(allowed);

  const reportingClass = normalizeCategoryType(ctx.categoryType, ctx.categoryName);

  // 1. The section says so.
  if (reportingClass === 'drink' && permitted.has('drink')) return 'drink';

  // 2. The business type's own default (catering → package). Not applied to a
  //    drink section, which step 1 already had first refusal on.
  const typeDefault = defaultItemKindForBusinessType(ctx.typeId);
  if (typeDefault !== DEFAULT_ITEM_KIND && permitted.has(typeDefault)) {
    // A caterer filing something under "Other" is describing a service line
    // (staff hours, corkage, hire) far more often than a food package.
    if (reportingClass === 'other' && permitted.has('service')) return 'service';
    return typeDefault;
  }

  // 3. A dish is the universal food line, and `resolveKindRelevance` guarantees
  //    it is always allowed.
  return DEFAULT_ITEM_KIND;
}

/**
 * Whether this branch should be offered a "Build a combo" entry point next to
 * "Add item". True only when a sub-type DECLARES combos as a primary kind —
 * inference never reaches for `combo` on its own (see the header).
 */
export function shouldOfferComboEntry(ctx: {
  typeId: string | null | undefined;
  specificType?: string | null;
  activeModes?: readonly ActiveMode[];
}): boolean {
  return resolveKindRelevance(ctx).primary.includes('combo');
}
